import React, {createContext, useContext, useState, useCallback, useEffect} from 'react';
import {useAppSelector} from "../../app/store";
import {appStatusNeedAcceptOfferSelector} from "../../common/reducers/appStatus";
import {useIsMobile} from "../../common/utils/useIsMobile";

type OfferContextType = {
  showOfferModal: boolean;
  showOfferScreen: boolean;
  openOfferModal: (viewOnly?: boolean) => void;
  openOfferScreen: (viewOnly?: boolean) => void;
  closeOfferModal: () => void;
  closeOfferScreen: () => void;
  offerDismissed: boolean;
  setOfferDismissed: (value: boolean) => void;
  needAcceptOffer: boolean;
  isViewOnly: boolean;
};

const OfferContext = createContext<OfferContextType | null>(null);

export const OfferProvider: React.FC = ({children}) => {
  const needAcceptOffer = useAppSelector(appStatusNeedAcceptOfferSelector);
  const isMobile = useIsMobile();

  const [showOfferModal, setShowOfferModal] = useState(false);
  const [showOfferScreen, setShowOfferScreen] = useState(false);
  const [offerDismissed, setOfferDismissed] = useState(false);
  const [isViewOnly, setIsViewOnly] = useState(false);

  const openOfferModal = useCallback((viewOnly = false) => {
    setIsViewOnly(viewOnly);
    setShowOfferModal(true);
  }, []);

  const openOfferScreen = useCallback((viewOnly = false) => {
    setIsViewOnly(viewOnly);
    setShowOfferScreen(true);
  }, []);

  const closeOfferModal = useCallback(() => {
    setShowOfferModal(false);
    setIsViewOnly(false);
  }, []);

  const closeOfferScreen = useCallback(() => {
    setShowOfferScreen(false);
    setIsViewOnly(false);
  }, []);

  // Автоматически показывать модалку/экран когда нужно принять оферту
  useEffect(() => {
    if (needAcceptOffer && !offerDismissed) {
      setIsViewOnly(false);
      if (isMobile) {
        setShowOfferScreen(true);
      } else {
        setShowOfferModal(true);
      }
    }
  }, [needAcceptOffer, offerDismissed, isMobile]);

  return (
    <OfferContext.Provider value={{
      showOfferModal,
      showOfferScreen,
      openOfferModal,
      openOfferScreen,
      closeOfferModal,
      closeOfferScreen,
      offerDismissed,
      setOfferDismissed,
      needAcceptOffer,
      isViewOnly,
    }}>
      {children}
    </OfferContext.Provider>
  );
};

export const useOffer = (): OfferContextType => {
  const context = useContext(OfferContext);
  if (!context) {
    throw new Error('useOffer must be used within OfferProvider');
  }
  return context;
};
